import { useEffect, useRef, useState } from "react";
import { api, resolveImage } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Trash2,
  Plus,
  Save,
  ArrowUp,
  ArrowDown,
  Image as ImageIcon,
  Video,
  Youtube,
  Link as LinkIcon,
} from "lucide-react";

const KIND_LABEL = { image: "Imagen", video: "Video" };
const SOURCE_LABEL = {
  upload: "Subir archivo",
  youtube: "YouTube",
  vimeo: "Vimeo",
  url: "URL externa (mp4 / img)",
};

function MediaPreview({ kind, source, url, thumbnail }) {
  if (!url) {
    return (
      <div className="w-full h-32 rounded-lg bg-[var(--c-bg-deep)]/60 grid place-items-center text-[var(--c-text)]/40 text-xs">
        Sin medio
      </div>
    );
  }
  if (kind === "image") {
    return (
      <img
        src={resolveImage(url)}
        alt=""
        className="w-full h-32 object-cover rounded-lg border border-[var(--c-bio-green)]/15"
      />
    );
  }
  // video
  if (source === "youtube" || source === "vimeo") {
    return (
      <div className="w-full h-32 rounded-lg bg-[var(--c-bg-deep)] border border-[var(--c-bio-green)]/15 grid place-items-center text-[var(--c-text)]/60 text-xs gap-1">
        {source === "youtube" ? <Youtube className="w-6 h-6" /> : <Video className="w-6 h-6" />}
        <span className="truncate max-w-full px-2">{url}</span>
      </div>
    );
  }
  return (
    <video
      src={resolveImage(url)}
      poster={thumbnail ? resolveImage(thumbnail) : undefined}
      className="w-full h-32 object-cover rounded-lg border border-[var(--c-bio-green)]/15"
      muted
      preload="metadata"
    />
  );
}

export default function MediaEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/media");
      const sorted = (res.data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(sorted);
    } catch {
      toast.error("No se pudo cargar la galería");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addNew = async () => {
    try {
      const order = items.length ? Math.max(...items.map((i) => i.order || 0)) + 1 : 0;
      const res = await api.post("/media", {
        kind: "image",
        source: "url",
        url: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1600&q=85",
        title: "Nueva imagen",
        subtitle: "",
        order,
      });
      setItems((prev) => (prev.some((p) => p.id === res.data.id) ? prev : [...prev, res.data]));
      toast.success("Item creado — edítalo abajo");
    } catch {
      toast.error("No se pudo crear");
    }
  };

  const patch = (id, partial) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...partial } : it)));

  const save = async (item) => {
    try {
      const { id, created_at, ...payload } = item;
      await api.put(`/media/${id}`, payload);
      toast.success("Guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.title || item.url}"?`)) return;
    try {
      await api.delete(`/media/${item.id}`);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      toast.success("Eliminado");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx];
    const b = items[j];
    const next = items.slice();
    next[idx] = { ...b, order: a.order ?? idx };
    next[j] = { ...a, order: b.order ?? j };
    // Normalize sequentially to avoid duplicates
    next.forEach((it, k) => (it.order = k));
    setItems(next);
    try {
      await Promise.all(
        next.map((it) => api.put(`/media/${it.id}`, { order: it.order }))
      );
    } catch {
      toast.error("No se pudo reordenar");
    }
  };

  const onPickFile = async (item, file) => {
    if (!file) return;
    setUploadingId(item.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { url, kind } = res.data;
      patch(item.id, { url, kind, source: "upload" });
      // Auto-save after upload
      await api.put(`/media/${item.id}`, { url, kind, source: "upload" });
      toast.success(`Subido (${kind})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div data-testid="media-editor" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-3xl text-[var(--c-text)]">Galería · Imágenes y videos</h2>
          <p className="text-sm text-[var(--c-text)]/60 mt-1">
            Carrusel mostrado entre &quot;La comunidad&quot; y &quot;Preventa&quot;. Si la lista está vacía, la sección se oculta automáticamente.
          </p>
        </div>
        <Button
          onClick={addNew}
          data-testid="media-add"
          className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] rounded-full font-semibold"
        >
          <Plus className="w-4 h-4 mr-1" /> Agregar item
        </Button>
      </div>

      {loading && (
        <p className="text-[var(--c-text)]/60 text-sm">Cargando…</p>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[var(--c-bio-green)]/30 bg-[var(--c-bg-forest)]/40 p-10 text-center text-[var(--c-text)]/60">
          Aún no hay imágenes ni videos. Agrega el primero con el botón de arriba.
        </div>
      )}

      <div className="grid gap-5">
        {items.map((item, idx) => (
          <div
            key={item.id}
            data-testid={`media-card-${item.id}`}
            className="rounded-2xl border border-[var(--c-bio-green)]/20 bg-[var(--c-bg-forest)]/70 backdrop-blur p-5 grid lg:grid-cols-[280px_1fr] gap-5"
          >
            <div className="space-y-3">
              <MediaPreview {...item} />
              <input
                ref={(el) => (fileRefs.current[item.id] = el)}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) onPickFile(item, f);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRefs.current[item.id]?.click()}
                disabled={uploadingId === item.id}
                data-testid={`media-upload-${item.id}`}
                className="w-full bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-firefly-gold)] hover:text-[#0B1A0F] hover:border-[var(--c-firefly-gold)]"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {uploadingId === item.id ? "Subiendo…" : "Subir desde dispositivo"}
              </Button>
              <p className="text-[10px] text-[var(--c-text)]/40 leading-tight">
                Imágenes ≤ 8 MB · Videos ≤ 80 MB (mp4 / webm / mov)
              </p>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)]">Tipo</label>
                  <Select
                    value={item.kind}
                    onValueChange={(v) => patch(item.id, { kind: v })}
                  >
                    <SelectTrigger className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--c-bg-forest)] text-[var(--c-text)] border-[var(--c-bio-green)]/25">
                      <SelectItem value="image">{KIND_LABEL.image}</SelectItem>
                      <SelectItem value="video">{KIND_LABEL.video}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)]">Origen</label>
                  <Select
                    value={item.source}
                    onValueChange={(v) => patch(item.id, { source: v })}
                  >
                    <SelectTrigger className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--c-bg-forest)] text-[var(--c-text)] border-[var(--c-bio-green)]/25">
                      <SelectItem value="upload">{SOURCE_LABEL.upload}</SelectItem>
                      {item.kind === "video" && (
                        <>
                          <SelectItem value="youtube">{SOURCE_LABEL.youtube}</SelectItem>
                          <SelectItem value="vimeo">{SOURCE_LABEL.vimeo}</SelectItem>
                        </>
                      )}
                      <SelectItem value="url">{SOURCE_LABEL.url}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)] flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" />
                  {item.source === "youtube"
                    ? "URL o ID de YouTube"
                    : item.source === "vimeo"
                    ? "URL o ID de Vimeo"
                    : item.source === "url"
                    ? "URL externa (https://...)"
                    : "URL del archivo (se autocompleta al subir)"}
                </label>
                <Input
                  value={item.url || ""}
                  onChange={(e) => patch(item.id, { url: e.target.value })}
                  data-testid={`media-url-${item.id}`}
                  className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]"
                />
              </div>

              {item.kind === "video" && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)]">Miniatura (opcional)</label>
                  <Input
                    value={item.thumbnail || ""}
                    onChange={(e) => patch(item.id, { thumbnail: e.target.value })}
                    placeholder="URL de la imagen preview"
                    className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)]">Título</label>
                  <Input
                    value={item.title || ""}
                    onChange={(e) => patch(item.id, { title: e.target.value })}
                    className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)]">Orden</label>
                  <Input
                    type="number"
                    value={item.order ?? 0}
                    onChange={(e) => patch(item.id, { order: parseInt(e.target.value || "0", 10) })}
                    className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--c-firefly-gold)]">Subtítulo / descripción corta</label>
                <Textarea
                  rows={2}
                  value={item.subtitle || ""}
                  onChange={(e) => patch(item.id, { subtitle: e.target.value })}
                  className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] resize-none"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => save(item)}
                  data-testid={`media-save-${item.id}`}
                  className="bg-[var(--c-bio-green)] hover:bg-[var(--c-firefly-gold)] hover:text-[#0B1A0F] text-[#0B1A0F] font-semibold"
                >
                  <Save className="w-3.5 h-3.5 mr-1" /> Guardar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-bg-deep)]"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-bg-deep)]"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => remove(item)}
                  data-testid={`media-delete-${item.id}`}
                  className="bg-transparent border-red-400/30 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
